'use client'
import { useSearchParams } from 'next/navigation'
import { useCallback, useEffect, useState } from 'react'

import { useQuinceRouter as useRouter } from '@src/modules/navigation'
import { _intersection, tranformToMap } from '@src/utils/general.utils'

type TQueryState<T extends string> = Record<T, string | undefined>

const getUnwatchedQueryString = ({
  searchParams,
  queryKeys,
}: {
  searchParams: URLSearchParams
  queryKeys: string[]
}) => {
  const unwatchedMap = {} as Record<string, string>
  for (const key of searchParams.keys()) {
    if (!queryKeys.includes(key)) {
      unwatchedMap[key] = searchParams.get(key) ?? ''
    }
  }
  return new URLSearchParams(unwatchedMap).toString()
}

export const createTargetURL = <T extends string>({
  updatedWatchedQueryState,
  searchParams,
  queryKeys,
}: {
  updatedWatchedQueryState: TQueryState<T>
  searchParams: URLSearchParams
  queryKeys: string[]
}) => {
  const unwatchedQueryString = getUnwatchedQueryString({ searchParams, queryKeys })

  // Build watched query string, but handle filterV2 without URL encoding
  const watchedParts: string[] = []
  const stateMap = tranformToMap(updatedWatchedQueryState)
  for (const [key, value] of Object.entries(stateMap)) {
    if (value !== undefined && value !== '') {
      if (key === 'filterV2') {
        // filterV2 should not be URL-encoded (uses _ and __ as delimiters instead of = and &)
        watchedParts.push(`${key}=${value}`)
      } else {
        watchedParts.push(`${key}=${encodeURIComponent(value)}`)
      }
    }
  }
  const watchedQueryString = watchedParts.join('&')
  if (!unwatchedQueryString && !watchedQueryString) return ''
  if (unwatchedQueryString && watchedQueryString) return `?${unwatchedQueryString}&${watchedQueryString}`
  return `?${unwatchedQueryString || watchedQueryString}`
}

export function useAllQueryState<T extends string>(
  queryKeys: T[],
  fallback?: TQueryState<T>,
  shouldReplace?: boolean,
  shallow = false,
): [
  TQueryState<T>,
  (
    newQueryState: TQueryState<T>,
    navigateToOverwrite: string | null,
    autoScroll?: boolean,
    skipNavigation?: boolean,
  ) => string,
] {
  const searchParams = useSearchParams()
  const router = useRouter()

  const parseAllWatchedQuery = useCallback<() => TQueryState<T>>(() => {
    const searchParamKeys = Array.from(searchParams.keys())
    const queriedSearchParamKeys = _intersection(searchParamKeys, queryKeys)

    if (queriedSearchParamKeys.length === 0) {
      return fallback ?? ({} as TQueryState<T>)
    }

    const allQueryStates = {} as TQueryState<T>
    queryKeys.forEach(queryKey => {
      const queryValues = searchParams.get(queryKey)
      if (queryValues) allQueryStates[queryKey] = queryValues
    })
    return allQueryStates
  }, [searchParams, fallback, queryKeys])

  const [allWatchedQuery, setAllWatchedQuery] = useState<TQueryState<T>>(parseAllWatchedQuery)

  useEffect(() => {
    setAllWatchedQuery(parseAllWatchedQuery)
  }, [parseAllWatchedQuery])

  const setQueryStateHandler = (
    updatedWatchedQueryState: TQueryState<T>,
    navigateToOverwrite: string | null,
    autoScroll = true,
    skipNavigation = false,
  ) => {
    // TODO: read only watched queries
    setAllWatchedQuery(updatedWatchedQueryState)
    const targetURL =
      navigateToOverwrite ??
      createTargetURL({
        updatedWatchedQueryState,
        searchParams,
        queryKeys,
      })

    // Safely get pathname, fallback to current router pathname
    let pathname = ''
    try {
      pathname = globalThis?.location?.pathname || ''
    } catch (error) {
      // During client-side navigation, window.location might be invalid
      pathname = ''
    }

    // If pathname is empty, try to get it from router
    if (!pathname && router.pathname) {
      pathname = router.pathname
    }

    if (skipNavigation) {
      globalThis.history?.replaceState(globalThis.history?.state, '', pathname + targetURL)
    } else if (shouldReplace) {
      router.replace(pathname + targetURL, undefined, { scroll: autoScroll, shallow })
    } else {
      router.push(pathname + targetURL, { scroll: autoScroll, shallow })
    }
    return targetURL
  }

  return [allWatchedQuery, setQueryStateHandler]
}
